import { ChartEntry } from '../types';

// Helper function to extract text between asterisks and clean the remaining text
const cleanAnnotations = (text: string): { cleanedText: string; message: string } => {
  const messageMatch = text.match(/\*(.*?)\*/);
  const message = messageMatch ? messageMatch[1].trim() : '';
  const cleanedText = text.replace(/\*.*?\*/g, '').trim();
  return { cleanedText, message };
};

// Helper function to check if a string is numeric
const isNumeric = (str: string): boolean => {
  if (typeof str !== 'string') return false; // we only process strings!
  return !isNaN(parseFloat(str)) && isFinite(Number(str));
};

// Helper function to check if a string is a re-entry value (case-insensitive)
const isReEntry = (str: string): boolean => {
  const val = str?.trim().toUpperCase();
  return val === 'RE' || val === 'RE-ENTRY';
};

export const processChartText = (text: string): ChartEntry[] => {
  const entries: ChartEntry[] = [];

  // Keep raw lines (preserving blanks) for vertical format where empty LW fields are valid
  const rawLines = text.split('\n').map(line => line.trim());
  // Filtered lines (no blanks) for horizontal/fallback formats
  const lines = rawLines.filter(line => line.length > 0);

  if (lines.length === 0) {
      console.warn("[processChartText] Input text is empty or contains only whitespace.");
      return [];
  }

  // --- Format Detection ---
  const expectedHeaders = ['tw', 'lw', 'peak', 'weeks', 'song', 'artist'];

  // Find where the headers start in raw lines (skip any title/intro lines)
  let headerStartIndex = 0;
  for (let i = 0; i < rawLines.length - 5; i++) {
    if (rawLines[i].toLowerCase() === 'tw' &&
        rawLines[i + 1]?.toLowerCase() === 'lw' &&
        rawLines[i + 2]?.toLowerCase() === 'peak') {
      headerStartIndex = i;
      console.log(`[processChartText] Found headers starting at line ${i} (skipped ${i} intro lines)`);
      break;
    }
  }

  const headerLines = rawLines.slice(headerStartIndex, headerStartIndex + 6).map(l => l.toLowerCase());
  let isNewVerticalMultiLineFormat =
    headerLines.length === 6 &&
    headerLines.every((line, index) => line === expectedHeaders[index]);
  let dataStartIndex = headerStartIndex + 6;

  // Some chart emails leave the TW header cell blank, so the pasted headers are
  // just LW/Peak/Weeks/Song/Artist. Data rows still carry all 6 lines (TW first).
  if (!isNewVerticalMultiLineFormat) {
    const headersWithoutTw = expectedHeaders.slice(1);
    for (let i = 0; i <= rawLines.length - headersWithoutTw.length; i++) {
      if (headersWithoutTw.every((h, k) => rawLines[i + k].toLowerCase() === h)) {
        isNewVerticalMultiLineFormat = true;
        headerStartIndex = i;
        dataStartIndex = i + headersWithoutTw.length;
        console.log(`[processChartText] Found headers without TW starting at line ${i}`);
        break;
      }
    }
  }

  // Header row may be preceded by a title line (e.g. "Friday PRELIM Hot 100 dated ...")
  const horizontalHeaderIndex = lines.findIndex(line => /^TW[\t\s]+LW/i.test(line));
  const isOldHorizontalFormat = horizontalHeaderIndex !== -1;

  // --- Parsing Logic --- 

  if (isNewVerticalMultiLineFormat) {
    console.log("[processChartText] Detected NEW Vertical Headers / Multi-Line Entry format.");
    console.log(`[processChartText] Total raw lines: ${rawLines.length}, headers start at: ${headerStartIndex}`);
    console.log(`[processChartText] First 20 raw lines:`, rawLines.slice(0, 20));
    // Start processing after the 6 header lines, stepping by 6 (TW, LW, Pk, Wk, Song, Artist)
    // Use rawLines to preserve blank lines (e.g. empty LW for new entries)
    for (let i = dataStartIndex; i < rawLines.length; i += 6) { // STEP BY 6
        const twLine = rawLines[i];
        const lwLine = rawLines[i + 1];
        const peakLine = rawLines[i + 2];
        const weeksLine = rawLines[i + 3];
        const songLine = rawLines[i + 4];
        const artistLine = rawLines[i + 5];

        // Basic validation: ensure all six lines exist for the entry
        if (
            twLine === undefined || lwLine === undefined || peakLine === undefined || 
            weeksLine === undefined || songLine === undefined || artistLine === undefined
        ) {
            console.warn(`[processChartText - New Vertical] Skipping entry starting at index ${i}: Missing expected 6 lines.`);
            continue; // Skip this incomplete entry group
        }

        // 1. Parse individual data lines
        if (!isNumeric(twLine)) {
             console.warn(`[processChartText - New Vertical] Skipping entry at index ${i}: TW line is not numeric. Line: "${twLine}"`);
             continue;
        }
        
        try {
            const position = parseInt(twLine, 10);
            const lastWeek = isNumeric(lwLine) ? parseInt(lwLine, 10) : (isReEntry(lwLine) ? 'RE' : 0);
            const peakStr = peakLine;
            const weeksStr = weeksLine;
            const peak = parseInt(peakStr, 10);
            const weeks = parseInt(weeksStr, 10);

            // Debug logging for parsed values
            console.log(`[processChartText - New Vertical] Entry at index ${i}: TW=${twLine} LW=${lwLine}(parsed:${lastWeek}) Peak=${peakStr} Weeks=${weeksStr} Song=${songLine}`);

            // 2. Parse Song Line (Clean annotations and extract message)
            const { cleanedText: song, message } = cleanAnnotations(songLine);

            // 3. Parse Artist Line
            const artist = artistLine.trim(); // Artist is the whole line

            // 4. Validate and Push Entry
            if (!isNaN(position) && song && artist && !isNaN(peak) && !isNaN(weeks)) {
                entries.push({
                    position,
                    lastWeek,
                    song,
                    artist,
                    peak,
                    weeks,
                    message: message || undefined,
                });
            } else {
                console.warn(`[processChartText - New Vertical] Skipping entry at index ${i}: Failed to parse required fields. Pos: ${position}, Peak: ${peak}, Weeks: ${weeks}, Song: ${song}, Artist: ${artist}`);
            }
        } catch (e) {
            console.error(`[processChartText - New Vertical] Error processing entry at index ${i}:`, e);
            // Log the individual lines for easier debugging
            console.error(`  TW Line: "${twLine}"`);
            console.error(`  LW Line: "${lwLine}"`);
            console.error(`  Peak Line: "${peakLine}"`);
            console.error(`  Weeks Line: "${weeksLine}"`);
            console.error(`  Song Line: "${songLine}"`);
            console.error(`  Artist Line: "${artistLine}"`);
        }
    }

  } else if (isOldHorizontalFormat) {
    console.log("[processChartText] Detected OLD Horizontal (Single Line) format.");
    for (let i = horizontalHeaderIndex + 1; i < lines.length; i++) { // Start after the header row
      const originalLine = lines[i];
      const { cleanedText: cleanedLine, message } = cleanAnnotations(originalLine);
      const columns = cleanedLine.split(/\s{2,}|\t/).map(col => col.trim()).filter(col => col.length > 0);
      
      console.log(`[processChartText - Old Horizontal] Processing line ${i}: "${originalLine}" -> Cleaned: "${cleanedLine}"`);
      console.log(`[processChartText - Old Horizontal] Split columns:`, columns);

      if (columns.length >= 4 && isNumeric(columns[0])) {
        try {
          const position = parseInt(columns[0], 10);

          // Determine how many of LW/Peak/Weeks are present before Song/Artist
          // We know columns[0] is TW (numeric). We need to figure out which columns are LW, Peak, Weeks vs Song, Artist.
          // Strategy: scan forward from column 1 to find where numeric fields end and Song (non-numeric text) begins.
          let numericEndIndex = 1; // Start checking from column 1
          while (numericEndIndex < columns.length && isNumeric(columns[numericEndIndex])) {
            numericEndIndex++;
          }
          // Also check for RE/RE-ENTRY/NEW as valid LW values (NEW parses to lastWeek 0 below)
          if (numericEndIndex === 1 && columns.length > 1 && (isReEntry(columns[1]) || columns[1].toUpperCase() === 'NEW')) {
            numericEndIndex = 2;
            while (numericEndIndex < columns.length && isNumeric(columns[numericEndIndex])) {
              numericEndIndex++;
            }
          }

          // numericEndIndex now points to the first non-numeric column (Song)
          // The numeric columns between position and song are some subset of [LW, Peak, Weeks]
          const numericCols = columns.slice(1, numericEndIndex);
          const textCols = columns.slice(numericEndIndex);

          let lastWeek: number | string = 0;
          let peak = NaN;
          let weeks = NaN;

          if (numericCols.length === 3) {
            // All three present: LW, Peak, Weeks
            lastWeek = isNumeric(numericCols[0]) ? parseInt(numericCols[0], 10) : (isReEntry(numericCols[0]) ? 'RE' : 0);
            peak = parseInt(numericCols[1], 10);
            weeks = parseInt(numericCols[2], 10);
          } else if (numericCols.length === 2) {
            // Two numeric cols: could be [LW, Weeks] or [Peak, Weeks] - assume [Peak, Weeks] with LW missing
            // Since Peak is typically smaller and Weeks is typically larger, use position as heuristic
            // But more reliably: if LW is missing, the two remaining are Peak and Weeks
            lastWeek = 0;
            peak = parseInt(numericCols[0], 10);
            weeks = parseInt(numericCols[1], 10);
            console.log(`[processChartText - Old Horizontal] Line ${i}: Only 2 numeric cols detected [${numericCols.join(', ')}], assuming LW is missing.`);
          } else if (numericCols.length === 1) {
            // Only one numeric col: assume it's Weeks (most essential)
            lastWeek = 0;
            peak = position; // Default peak to current position
            weeks = parseInt(numericCols[0], 10);
            console.log(`[processChartText - Old Horizontal] Line ${i}: Only 1 numeric col detected [${numericCols[0]}], assuming only Weeks present.`);
          } else {
            // No numeric cols between position and song
            console.warn(`[processChartText - Old Horizontal] Line ${i}: No numeric cols found between position and song.`);
          }

          let song = '';
          let artist = '';

          if (textCols.length >= 2) {
            song = textCols[0];
            artist = textCols.slice(1).join(' ');
          } else if (textCols.length === 1) {
            song = textCols[0];
            artist = '';
            console.warn(`[processChartText - Old Horizontal] Line ${i}: Only 1 text column detected, assuming artist is missing.`);
          }

          song = song.trim();
          artist = artist.trim();

          if (!isNaN(position) && song && artist && !isNaN(peak) && !isNaN(weeks)) {
            entries.push({
              position,
              lastWeek,
              song,
              artist,
              peak,
              weeks,
              message: message || undefined,
            });
          } else {
              console.warn(`[processChartText - Old Horizontal] Skipping invalid line ${i}: Failed parse. Pos: ${position}, Peak: ${peak}, Weeks: ${weeks}, Song: ${song}, Artist: ${artist}`);
          }
        } catch (e) {
          console.warn(`[processChartText - Old Horizontal] Skipping line ${i}: Error parsing. Error: ${e}, Line: ${originalLine}`);
        }
      } else {
         console.warn(`[processChartText - Old Horizontal] Skipping line ${i}: Invalid columns/format. Cols: ${columns.length}, Line: "${cleanedLine}"`);
      }
    }
  } else {
    console.log("[processChartText] Assuming ORIGINAL Vertical (6 lines per entry) format.");
    let lineIndex = 0; // Track overall line number for logging
    
    // Process in chunks of 6 lines (Pos, LW, Pk, Wks, Song, Artist)
    for (let i = 0; i < lines.length; i += 6) {
        const chunk = lines.slice(i, i + 6);
        lineIndex = i;

        if (chunk.length !== 6) {
            console.warn(`[processChartText - Original Vertical] Skipping entry starting at line ${lineIndex}: Incomplete chunk of ${chunk.length} lines.`);
            continue;
        }

        try {
             const positionStr = chunk[0];
             const lastWeekStr = chunk[1];
             const peakStr = chunk[2];
             const weeksStr = chunk[3];
             const songStr = chunk[4];
             const artistStr = chunk[5];

             // Parse values, without defaulting
             const position = parseInt(positionStr, 10);
             const lastWeek = isNumeric(lastWeekStr) ? parseInt(lastWeekStr, 10) : (isReEntry(lastWeekStr) ? 'RE' : 0);
             const peak = parseInt(peakStr, 10);
             const weeks = parseInt(weeksStr, 10);
             const { cleanedText: song, message } = cleanAnnotations(songStr);
             const artist = artistStr.trim();

            // Validation to ensure all required values are parsed correctly
            if (!isNaN(position) && song && artist && !isNaN(peak) && !isNaN(weeks)) {
                entries.push({
                    position,
                    lastWeek,
                    song,
                    artist,
                    peak,
                    weeks,
                    message: message || undefined,
                });
            } else {
                 console.warn(`[processChartText - Original Vertical] Skipping entry at line ${lineIndex}: Invalid data. Pos: ${position}, Peak: ${peak}, Weeks: ${weeks}, Song: ${song}, Artist: ${artist}`);
            }
        } catch (e) {
             console.error(`[processChartText - Original Vertical] Error processing entry at line ${lineIndex}:`, e);
             console.error(`   Chunk:`, chunk);
        }
    }
  }

  console.log("[processChartText] Parsed entries:", entries);
  return entries;
}; 